import * as React from 'react';
import {RouteComponentProps} from 'react-router';
import {Dispatch} from 'redux';
import {connect} from 'react-redux';
import {SearchKeywordsCacheContext, IconButton, AppLogo} from '@openfin/desktop-ui';

import {LaunchBarSize} from '../../utils/constants';
import {Header} from '../../components/Panel/Header';
import {Panel} from '../../components/Panel/Panel';
import {ROUTES} from '../../../components/Router/consts';
import {getApps, AppStatus, getAppStatuses} from '../../../store/partials/Directory';
import {SetLaunchBarSize} from '../../../store/partials/LaunchBar';
import {getSearchEngines} from '../../../store/partials/SearchEngines';
import {RootState} from '../../../store/State';
import {App, SearchEngine} from '../../../types/commons';
import {isOmnibar} from '../../../utils/processHelpers';
import * as SearchIcon from '../../../assets/Search.svg';
import {AccountInfo} from '../../../store/partials/Authentication';
import * as CrossIcon from '../../../assets/Cross.svg';

import {EngineSearchResults} from './EngineSearchResults';
import {LauncherSearchResults} from './LauncherSearchResults';
import * as Styles from './SearchPanel.module.scss';

const cache: SearchKeywordsCacheContext = new Map();

interface Props extends RouteComponentProps {
    apps: App[];
    appStatuses: AppStatus;
    searchEngines: SearchEngine[];
    account: AccountInfo;
    setLaunchBarSize: (size: LaunchBarSize) => void;
}

export const SearchPanelComponent: React.FC<Props> = (props) => {
    const {apps, appStatuses, searchEngines, account, history, setLaunchBarSize} = props;
    const [search, setSearch] = React.useState('');
    const [searchEngine, setSearchEngine] = React.useState<SearchEngine | null>(null);
    const searchInputRef = React.useRef<HTMLInputElement | null>(null);

    const focusRef = React.useCallback((node) => {
        if (node !== null) {
            node.focus();
            searchInputRef.current = node;
        }
    }, []);

    const handleClose = () => {
        history.push(ROUTES.HOME);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        event.target.focus();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const key = event.keyCode || event.charCode;
        // key code 8 is backspace
        if (key === 8 && event.currentTarget.selectionStart === 0 && searchEngine) {
            clearSearchEngine();
            event.stopPropagation();
        }
    };

    const handlePanelBlur = (event: React.FocusEvent) => {
        event.stopPropagation();
        if (!search && !searchEngine) {
            history.push(ROUTES.HOME);
        }
    };

    const clearSearchEngine = () => {
        setSearchEngine(null);
    };

    if (isOmnibar()) {
        // Omnibar HOME is search panel so escape doesn't clear search field
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                setSearch('');
            }
        });
    }

    React.useEffect(() => {
        cache.clear();
    }, [apps]);

    React.useEffect(() => {
        // Show panel only when search text has been entered
        const query = search.trim();

        if (query.length > 0) {
            // Expand the panel, to display results
            setLaunchBarSize(LaunchBarSize.EXPANDED);
        } else {
            setLaunchBarSize(LaunchBarSize.NORMAL);
        }
    }, [search]);

    return (
        <Panel className={Styles['panel']} initialState={LaunchBarSize.NORMAL} onBlur={handlePanelBlur}>
            <Header title="Search" onClose={handleClose} icon={SearchIcon}>
                {
                    searchEngine && <div className={Styles['chip']} title={searchEngine.name} onClick={clearSearchEngine}>
                        <AppLogo src={searchEngine.icon} />
                    </div>
                }
                <input type="text"
                    placeholder="Search..."
                    onChange={handleChange}
                    value={search}
                    ref={focusRef}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown} />
                {search && <IconButton image={CrossIcon} onClick={() => setSearch('')} className={Styles['clear-button']} />}
            </Header>
            <div className={Styles['panel-content']}>
                {(search && searchEngine) && <EngineSearchResults
                    search={search}
                    searchEngine={searchEngine}
                    account={account}
                    keyboardEventSource={searchInputRef} />}
                {(search && !searchEngine) && <LauncherSearchResults
                    search={search}
                    apps={apps}
                    searchEngines={searchEngines}
                    appStatuses={appStatuses}
                    setSearch={setSearch}
                    setSearchEngine={setSearchEngine}
                    cache={cache}
                    keyboardEventSource={searchInputRef} />}
            </div>
        </Panel>
    );
};

const mapState = (state: RootState) => ({
    apps: getApps(state),
    appStatuses: getAppStatuses(state),
    searchEngines: getSearchEngines(state),
    account: state.authentication.account
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    setLaunchBarSize: (size: LaunchBarSize) => dispatch(new SetLaunchBarSize(size))
});

export const SearchPanel = connect(mapState, mapDispatchToProps)(SearchPanelComponent);
